import { Spin, Space } from "antd";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";

import { TextField } from "misc/fields";
import { Transfer } from "misc/transfer";
import { useSearchUser } from "misc/api/keycloakApi";
import ConditionalWrapper from "misc/conditional/ConditionalWrapper";

const UserTransfer = ({ defaultValue, value, onChange = () => null, disabled, disableItem = () => false, isLoading, ...props }) => {
    const [t] = useTranslation();

    const { searchUser, isLoading: isSearching } = useSearchUser();
    const [targetKeys, setTargetKeys] = useState(value ?? defaultValue ?? []);
    const [leftSearch, setLeftSearch] = useState(null);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [dataSource, setDataSource] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(false);

    useEffect(() => {
        const userIds = value ?? defaultValue ?? [];

        if (userIds.length > 0) {
            setIsInitialLoading(true);
            searchUser(
                { ids: userIds },
                {
                    onSuccess: res => {
                        setDataSource(res.users);
                        setIsInitialLoading(false);
                    },
                    onError: () => {
                        setIsInitialLoading(false);
                    }
                }
            );
        }
    }, []);

    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const handleUserSearch = async email => {
        if (!email || email.trim() === "" || !email.includes("@")) {
            return;
        }

        const emailToSearch = email.trim().toLowerCase();
        const userAlreadySelected = dataSource.filter(user => targetKeys.includes(user.id)).some(user => user.email.toLowerCase() === emailToSearch);

        if (userAlreadySelected) {
            return;
        }

        searchUser(
            { emails: [email.trim()] },
            {
                onSuccess: res => {
                    if (res.users && res.users.length > 0) {
                        const newUsers = res.users.filter(user => !dataSource.some(su => su.id === user.id));
                        if (newUsers.length > 0) {
                            setDataSource(prev => [...prev, ...newUsers]);
                        }
                    }
                }
            }
        );
    };

    const handleSearch = (direction, value) => {
        if (direction === "left") {
            setLeftSearch(value);

            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }

            if (value.includes("@")) {
                const newTimeout = setTimeout(() => {
                    handleUserSearch(value);
                }, 300);
                setSearchTimeout(newTimeout);
            }
        }
    };

    const handleChange = newTargetKeys => {
        setTargetKeys(newTargetKeys);
        onChange(newTargetKeys);
    };

    return (
        <ConditionalWrapper condition={isLoading || disabled || isInitialLoading} wrapper={children => <Spin indicator={null}>{children}</Spin>}>
            <Transfer
                listStyle={{
                    width: "100%"
                }}
                dataSource={dataSource.map(user => ({ ...user, disabled: disableItem(user, targetKeys) }))}
                rowKey={item => item.id}
                render={item => item.email}
                targetKeys={targetKeys}
                titles={[t("t_available_users"), t("t_selected_users")]}
                onChange={handleChange}
                filterOption={(inputValue, option) => option.email?.toLowerCase().includes(inputValue.toLowerCase())}
                onSearch={handleSearch}
                showSearch={true}
                locale={{
                    itemUnit: t("t_item"),
                    itemsUnit: t("t_items"),
                    searchPlaceholder: t("t_search_or_enter_email"),
                    notFoundContent: [
                        leftSearch ? (
                            <Space direction="vertical">
                                {!isSearching && (
                                    <div>
                                        <FontAwesomeIcon icon={["fas", "user-slash"]} size="xl" />
                                        <TextField value="t_user_transfer_empty_list" style={{ fontSize: 14 }} />
                                    </div>
                                )}
                                {leftSearch.includes("@") && isSearching && (
                                    <div>
                                        <Spin size="small" />
                                    </div>
                                )}
                            </Space>
                        ) : (
                            <Space direction="vertical">
                                <FontAwesomeIcon icon={["fas", "user-plus"]} size="xl" />
                                <TextField value="t_user_transfer_add_user_description" style={{ fontSize: 14 }} />
                            </Space>
                        )
                    ]
                }}
                disabled={disabled}
                {...props}
            />
        </ConditionalWrapper>
    );
};

UserTransfer.propTypes = {
    defaultValue: PropTypes.array,
    value: PropTypes.array,
    onChange: PropTypes.func,
    isLoading: PropTypes.bool,
    disabled: PropTypes.bool,
    disableItem: PropTypes.func
};

export default UserTransfer;
